import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  addResource,
  getCategories,
  getResourceTypes,
  updateResource,
  type Category,
  type CreateResourceInput,
  type Resource,
  type ResourceTypeEntity,
} from '../api/resourceService';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CategoryForm from './CategoryForm';
import ResourceTypeForm from './ResourceTypeForm';

export default function ResourceForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial?: Resource;
  onSaved?: (r: Resource) => void;
  onCancel?: () => void;
}) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language.startsWith('en') ? 'en' : 'es';
  const [categories, setCategories] = useState<Category[]>([]);
  const [resourceTypes, setResourceTypes] = useState<ResourceTypeEntity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isResourceTypeModalOpen, setIsResourceTypeModalOpen] = useState(false);

  type FormValues = {
    title_en: string;
    title_es: string;
    description_en: string;
    description_es: string;
    url: string;
    typeId: string; // Cambiado de 'type' a 'typeId' para mayor claridad
    categoryId: string;
    tags: string;
  };

  const { register, handleSubmit, formState: { errors }, watch } = useForm<FormValues>({
    defaultValues: initial
      ? {
        title_en: initial.title.en,
        title_es: initial.title.es,
        description_en: initial.description.en,
        description_es: initial.description.es,
        url: initial.url,
        typeId: initial.typeId,
        categoryId: initial.categoryId,
        tags: initial.tags.join(', '),
      }
      : {
        title_en: '',
        title_es: '',
        description_en: '',
        description_es: '',
        url: '',
        typeId: 'article',
        categoryId: '',
        tags: '',
      },
  });

  const watchedTitleEn = watch('title_en');
  const watchedTitleEs = watch('title_es');
  const watchedDescriptionEn = watch('description_en');
  const watchedDescriptionEs = watch('description_es');
  const watchedTags = watch('tags');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [cats, types] = await Promise.all([
          getCategories(),
          getResourceTypes()
        ]);
        if (!mounted) return;
        setCategories(cats);
        setResourceTypes(types);
      } catch (e) {
        if (!mounted) return;
        setError((e as Error).message);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const onSubmit = handleSubmit(async (values: FormValues) => {
    setIsLoading(true);
    setError(null);
    try {
      const payload: CreateResourceInput = {
        title: { en: values.title_en, es: values.title_es },
        description: { en: values.description_en, es: values.description_es },
        url: values.url,
        typeId: values.typeId,
        categoryId: values.categoryId,
        tags: values.tags.split(',').map(s => s.trim()).filter(Boolean),
      };

      const saved = initial
        ? await updateResource({ id: initial.id, ...payload })
        : await addResource(payload);

      onSaved?.(saved);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  });

  const openCategoryModal = () => setIsCategoryModalOpen(true);
  const openResourceTypeModal = () => setIsResourceTypeModalOpen(true);

  const handleCategorySaved = async () => {
    setIsCategoryModalOpen(false);
    // Recargar categorías
    try {
      const cats = await getCategories();
      setCategories(cats);
    } catch (e) {
      console.error('Error reloading categories:', e);
    }
  };

  const handleResourceTypeSaved = async () => {
    setIsResourceTypeModalOpen(false);
    // Recargar tipos de recursos
    try {
      const types = await getResourceTypes();
      setResourceTypes(types);
    } catch (e) {
      console.error('Error reloading resource types:', e);
    }
  };

  // Opciones para los selects
  const categoryOptions = useMemo(() => 
    categories.map(c => ({ id: c.id, label: c.name[lang] })), 
    [categories, lang]
  );

  const resourceTypeOptions = useMemo(() => 
    resourceTypes.map(t => ({ id: t.id, label: t.name[lang] })), 
    [resourceTypes, lang]
  );

  return (
    <div className="w-full max-w-4xl mx-auto max-h-[90vh] flex flex-col">
      {/* Header del formulario - STICKY ARRIBA */}
      <div className="sticky top-0 bg-[var(--surface)] z-10 p-6 pb-4 border-b border-[var(--border)]">
        <h3 className="text-3xl font-bold text-[var(--text-default)] mb-2">
          {initial ? t('resourceForm.editTitle') : t('resourceForm.title')}
        </h3>
        <p className="text-[var(--text-muted)]">
          {initial ? t('resourceForm.editSubtitle') : t('resourceForm.subtitle')}
        </p>
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="px-6 py-4 bg-[var(--surface)] border-b border-[var(--border)]">
          <div className="p-4 bg-[var(--border)] border border-[var(--text-default)] rounded-lg text-[var(--text-default)]">
            {error}
          </div>
        </div>
      )}

      {/* Formulario */}
      <div className="p-6 flex-1 overflow-y-auto">
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Campos de título en inglés y español */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--text-default)]">
                {t('resourceForm.titleEn')}
              </label>
              <div className="relative">
                <input
                  {...register('title_en', { 
                    required: t('resourceForm.validation.titleEnRequired'),
                    maxLength: { value: 60, message: t('resourceForm.validation.maxLength', { count: 60 }) }
                  })}
                  placeholder={t('resourceForm.titleEnPlaceholder')}
                  maxLength={60}
                  className="w-full px-4 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-[var(--text-muted)]">
                  {watchedTitleEn?.length || 0}/60
                </div>
              </div>
              {errors.title_en && (
                <p className="text-[var(--text-muted)] text-sm mt-1">{errors.title_en.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--text-default)]">
                {t('resourceForm.titleEs')}
              </label>
              <div className="relative">
                <input
                  {...register('title_es', { 
                    required: t('resourceForm.validation.titleEsRequired'),
                    maxLength: { value: 60, message: t('resourceForm.validation.maxLength', { count: 60 }) }
                  })}
                  placeholder={t('resourceForm.titleEsPlaceholder')}
                  maxLength={60}
                  className="w-full px-4 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-[var(--text-muted)]">
                  {watchedTitleEs?.length || 0}/60
                </div>
              </div>
              {errors.title_es && (
                <p className="text-[var(--text-muted)] text-sm mt-1">{errors.title_es.message}</p>
              )}
            </div>
          </div>

          {/* Campos de descripción en inglés y español */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--text-default)]">
                {t('resourceForm.descriptionEn')}
              </label>
              <div className="relative">
                <textarea
                  {...register('description_en', { 
                    required: t('resourceForm.validation.descriptionEnRequired'),
                    maxLength: { value: 200, message: t('resourceForm.validation.maxLength', { count: 200 }) }
                  })}
                  placeholder={t('resourceForm.descriptionEnPlaceholder')}
                  maxLength={200}
                  rows={3}
                  className="w-full px-4 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200 resize-none"
                />
                <div className="absolute right-3 bottom-3 text-xs text-[var(--text-muted)]">
                  {watchedDescriptionEn?.length || 0}/200
                </div>
              </div>
              {errors.description_en && (
                <p className="text-[var(--text-muted)] text-sm mt-1">{errors.description_en.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--text-default)]">
                {t('resourceForm.descriptionEs')}
              </label>
              <div className="relative">
                <textarea
                  {...register('description_es', { 
                    required: t('resourceForm.validation.descriptionEsRequired'),
                    maxLength: { value: 200, message: t('resourceForm.validation.maxLength', { count: 200 }) }
                  })}
                  placeholder={t('resourceForm.descriptionEsPlaceholder')}
                  maxLength={200}
                  rows={3}
                  className="w-full px-4 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200 resize-none"
                />
                <div className="absolute right-3 bottom-3 text-xs text-[var(--text-muted)]">
                  {watchedDescriptionEs?.length || 0}/200
                </div>
              </div>
              {errors.description_es && (
                <p className="text-[var(--text-muted)] text-sm mt-1">{errors.description_es.message}</p>
              )}
            </div>
          </div>

          {/* URL */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[var(--text-default)]">
              {t('resourceForm.url')}
            </label>
            <input
              {...register('url', { 
                required: t('resourceForm.validation.urlRequired'),
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: t('resourceForm.validation.urlInvalid')
                }
              })}
              placeholder={t('resourceForm.urlPlaceholder')}
              type="url"
              className="w-full px-4 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200"
            />
            {errors.url && (
              <p className="text-sm text-[var(--text-muted)]">{errors.url.message}</p>
            )}
          </div>

          {/* Tipo y Categoría */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--text-default)]">
                {t('resourceForm.resourceType')}
              </label>
              <div className="flex gap-2">
                <div className="relative w-3/4">
                  <select
                    {...register('typeId', { required: t('resourceForm.validation.typeRequired') })}
                    className="appearance-none w-full px-4 py-3 pr-10 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200"
                  >
                    <option value="">{t('resourceForm.selectResourceType')}</option>
                    {resourceTypeOptions.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <span className="material-icons absolute right-3 top-3 text-[var(--text-muted)] pointer-events-none text-lg">
                    expand_more
                  </span>
                </div>
                <button
                  type="button"
                  onClick={openResourceTypeModal}
                  className="w-1/4 h-12 flex items-center justify-center border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors duration-200"
                  title={t('resourceForm.addNewType')}
                >
                  <span className="material-icons text-lg">+</span>
                </button>
              </div>
              {errors.typeId && (
                <p className="text-sm text-[var(--text-muted)]">{errors.typeId.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--text-default)]">
                {t('resourceForm.category')}
              </label>
              <div className="flex gap-2">
                <div className="relative w-3/4">
                  <select
                    {...register('categoryId', { required: t('resourceForm.validation.categoryRequired') })}
                    className="appearance-none w-full px-4 py-3 pr-10 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200"
                  >
                    <option value="">{t('resourceForm.selectCategory')}</option>
                    {categoryOptions.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <span className="material-icons absolute right-3 top-3 text-[var(--text-muted)] pointer-events-none text-lg">
                    expand_more
                  </span>
                </div>
                <button
                  type="button"
                  onClick={openCategoryModal}
                  className="w-1/4 h-12 flex items-center justify-center border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors duration-200"
                  title={t('resourceForm.addNewCategory')}
                >
                  <span className="material-icons text-lg">+</span>
                </button>
              </div>
              {errors.categoryId && (
                <p className="text-sm text-[var(--text-muted)]">{errors.categoryId.message}</p>
              )}
            </div>
          </div>

          {/* Campo de tags */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[var(--text-default)]">
              {t('resourceForm.tags')}
            </label>
            <div className="relative">
              <input
                {...register('tags', {
                  validate: (value) => {
                    if (!value) return true;
                    const tags = value.split(',').map(s => s.trim()).filter(Boolean);
                    if (tags.length > 5) return t('resourceForm.validation.maxTags');
                    if (tags.some(tag => tag.length > 15)) return t('resourceForm.validation.maxTagLength');
                    return true;
                  }
                })}
                placeholder={t('resourceForm.tagsPlaceholder')}
                className="w-full px-4 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-[var(--text-muted)]">
                {watchedTags ? watchedTags.split(',').filter(t => t.trim()).length : 0}/5
              </div>
            </div>
            {errors.tags && (
              <p className="text-[var(--text-muted)] text-sm mt-1">{errors.tags.message}</p>
            )}
          </div>
        </form>
      </div>

      {/* Botones de acción - STICKY ABAJO */}
      <div className="sticky bottom-0 bg-[var(--surface)] z-10 p-6 pt-4 border-t border-[var(--border)]">
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 sm:flex-none px-6 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('resourceForm.actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            onClick={onSubmit}
            className="flex-1 sm:flex-none px-6 py-3 bg-[var(--text-default)] text-[var(--surface)] rounded-lg hover:bg-[var(--border)] transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? t('resourceForm.actions.save') : (initial ? t('resourceForm.actions.update') : t('resourceForm.actions.create'))}
          </button>
        </div>
      </div>

      {/* Modal de categorías */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 bg-[var(--text-default)]/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-xl max-w-3xl w-full max-h-[50vh] overflow-y-auto scrollbar-custom">
            <CategoryForm
              onSaved={handleCategorySaved}
              onCancel={() => setIsCategoryModalOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Modal de tipos de recursos */}
      {isResourceTypeModalOpen && (
        <div className="fixed inset-0 bg-[var(--text-default)]/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-xl max-w-3xl w-full max-h-[50vh] overflow-y-auto scrollbar-custom">
            <ResourceTypeForm
              onSaved={handleResourceTypeSaved}
              onCancel={() => setIsResourceTypeModalOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
